import React, { useState, useEffect, useRef, useCallback } from 'react';
import { sanitizeLinkMark } from '@techchange/pm-specs/src/specs/marks/link.js';
import PropTypes from 'prop-types';
import { EditorView } from 'prosemirror-view';
import { FormattedMessage, useIntl } from 'react-intl';
import { Input } from 'reactstrap';

import { IconButton } from '../../../../../../_shared';
import { messages } from '../../../../../../_shared/i18n/messages';

import { embedLink, removeLink } from '../commands';

import styles from './LinkInput.module.scss';
import { ignoreInFlightEvent } from '../../../../../../_shared/utils/ignoreInFlightEvent';

const propTypes = {

	/**
	 * The prosemirror editor view object.
	 */
	editorView: PropTypes.instanceOf(EditorView).isRequired,

	/**
	 * The URL of the selected link.
	 */
	href: PropTypes.string,

	/**
	 * The start position of the link-marked node.
	 */
	start: PropTypes.number,

	/**
	 * The end position of the link-marked node.
	 */
	end: PropTypes.number,

	/**
	 * Custom css class name for divider.
	 */
	dividerClass: PropTypes.string,

	/**
	 * Custom css class name for icon buttons.
	 */
	buttonClass: PropTypes.string,

	/**
	 * Set of merge tags that are allowed in links.
	 */
	mergeTags: PropTypes.shape({

		organization: PropTypes.shape({

			domain: PropTypes.bool,

		}),

		invitation: PropTypes.shape({

			id: PropTypes.bool,

		}),

	}),

	/**
	 * Function to call after removeing link.
	 */
	onRemove: PropTypes.func,

};

const LinkInput = (props) => {

	const {
		href,
		dividerClass,
		buttonClass,
		editorView,
		start,
		end,
		mergeTags,
		onRemove,
	} = props;

	const [inputValue, setInputValue] = useState(href || '');
	const [editing, setEditing] = useState(!href);
	const [invalidURL, setInvalidURL] = useState(false);

	const inputRef = useRef();

	const intl = useIntl();

	useEffect(() => {

		if (inputRef.current && editing) {
			inputRef.current.focus({ preventScroll: true });
		}

	}, [editing]);

	const handleAddLink = useCallback((link) => {

		embedLink({ href: link }, start, end)(editorView.state, editorView.dispatch);
		editorView.focus();

	}, [editorView, end, start]);

	const handleRemoveLink = () => {

		removeLink(start, end)(editorView.state, editorView.dispatch);

		// Handle callback if provided
		if (onRemove) {
			onRemove();
		}

	};

	const handleValidateLink = () => {

		try {

			sanitizeLinkMark({ attrs: { href: inputValue } }, { mergeTags });

			setEditing(false);
			handleAddLink(inputValue);

		} catch (error) {

			setInvalidURL(true);
			inputRef.current.focus({ preventScroll: true });

		}

	};

	const handleInputChange = (event) => {

		setInputValue(event.target.value);
		setInvalidURL(false);

	};

	/**
	 * Listen for 'Enter' keydown on window and submit link if pressed.
	 */
	useEffect(() => {

		const handleEnterKey = ignoreInFlightEvent((event) => {

			if (event.key === 'Enter') {

				try {

					const value = event.target.value;

					event.preventDefault();

					sanitizeLinkMark({ attrs: { href: value } }, { mergeTags });

					setEditing(false);
					handleAddLink(value);

				} catch (error) {

					setInvalidURL(true);
					inputRef.current.focus({ preventScroll: true });

				}

			}

		});

		window.addEventListener('keydown', handleEnterKey);

		return () => {
			window.removeEventListener('keydown', handleEnterKey);
		};

	}, [handleAddLink, mergeTags]);

	return (
		<div
			className={styles['link-input']}
			className="d-flex align-items-center">
			{ editing
				? (
					<div className="d-flex flex-column">
						<Input
							innerRef={inputRef}
							className={`p-1 ${styles['url-input']}`}
							name="inputValue"
							value={inputValue}
							placeholder={intl.formatMessage(messages.TC_COMPONENTS_TEXT_EDITOR_LINK_PLACEHOLDER)}
							spellCheck={false}
							invalid={invalidURL}
							onChange={handleInputChange} />
						{ invalidURL &&
							<span className={styles['invalid-message']}>
								<FormattedMessage
									{...messages.TC_COMPONENTS_FORM_ERROR_URL} />
							</span>
						}
					</div>
				) : (
					<a
						className={`p-1 ${styles['link-preview']}`}
						href={inputValue}
						rel="noopener noreferrer nofollow"
						target="_blank">
						{inputValue}
					</a>
				)
			}
			<div className="d-flex">
				<div
					className={`${dividerClass || ''} mx-2 ${dividerClass ? '' : styles.divider}`} />
				{editing ? (
					<IconButton
						className={`${buttonClass || ''} p-1`}
						size="25px"
						title={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_DONE_LABEL)}
						icon="fas fa-check"
						onClick={handleValidateLink} />
				) : (
					<div className="d-flex align-items-center">
						<IconButton
							className={`${buttonClass || ''} p-1 me-2`}
							size="25px"
							title={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_EDIT_LABEL)}
							icon="fas fa-pen"
							onClick={() => setEditing(true)} />
						<IconButton
							className={`${buttonClass || ''} p-1`}
							size="25px"
							title={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_DELETE_LABEL)}
							icon="fas fa-trash"
							onClick={handleRemoveLink} />
					</div>
				)}
			</div>
		</div>
	);

};

LinkInput.propTypes = propTypes;

export default LinkInput;
